import { useEffect, useRef, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import {
  ghostBtnClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgMaterial, MfgRecipeSummary } from "@/lib/manufacturing/types";
import { ManufacturingNamedVariantsBulkImportModal } from "./ManufacturingNamedVariantsBulkImportModal";
import { ManufacturingNamedVariantsListCard } from "./ManufacturingNamedVariantsListCard";
import { ManufacturingNamedVariantApplicationsList } from "./ManufacturingNamedVariantApplicationsList";
import { ManufacturingNamedVariantApplyRecipesPicker } from "./ManufacturingNamedVariantApplyRecipesPicker";
import { ManufacturingNamedVariantDefaultTransformEditor } from "./ManufacturingNamedVariantDefaultTransformEditor";
import { ManufacturingNamedVariantExampleRecipePicker } from "./ManufacturingNamedVariantExampleRecipePicker";
import { ManufacturingNamedVariantMetaFields } from "./ManufacturingNamedVariantMetaFields";
import { ManufacturingNamedVariantPreviewModal } from "./ManufacturingNamedVariantPreviewModal";
import { useEntityBulkSelection } from "./useEntityBulkSelection";
import { useManufacturingNamedVariantsExport } from "./useManufacturingNamedVariantsExport";
import { useManufacturingNamedVariantsTab } from "./useManufacturingNamedVariantsTab";
import { useNamedVariantApplicationPreview } from "./useNamedVariantApplicationPreview";

export function ManufacturingNamedVariantsPanel({
  enabled,
  refreshTick,
  recipes,
  materials = [],
  orgSlotLabels,
  canArchive = false,
  canDelete = false,
  focusNamedVariantId = null,
  onFocusedNamedVariantConsumed,
}: {
  enabled: boolean;
  refreshTick: number;
  recipes: MfgRecipeSummary[];
  materials?: readonly MfgMaterial[];
  orgSlotLabels: readonly string[];
  canArchive?: boolean;
  canDelete?: boolean;
  focusNamedVariantId?: string | null;
  onFocusedNamedVariantConsumed?: () => void;
}) {
  const v = useManufacturingNamedVariantsTab(enabled, refreshTick);
  const bulk = useEntityBulkSelection(v.variants, v.variants);
  const exp = useManufacturingNamedVariantsExport();
  const preview = useNamedVariantApplicationPreview(v.applications);
  const [applicationsOpen, setApplicationsOpen] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const appsHydratedRef = useRef(false);
  const prevAppCountRef = useRef(0);

  useEffect(() => {
    if (!focusNamedVariantId) return;
    v.pickVariant(focusNamedVariantId);
    onFocusedNamedVariantConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- focus once when id arrives
  }, [focusNamedVariantId]);

  useEffect(() => {
    setApplicationsOpen(false);
    appsHydratedRef.current = false;
    prevAppCountRef.current = 0;
  }, [v.selectedId]);

  useEffect(() => {
    if (!v.selectedId || v.detailBusy) return;
    if (!appsHydratedRef.current) {
      appsHydratedRef.current = true;
      prevAppCountRef.current = v.applications.length;
      return;
    }
    if (v.applications.length > prevAppCountRef.current) {
      setApplicationsOpen(true);
    }
    prevAppCountRef.current = v.applications.length;
  }, [v.selectedId, v.detailBusy, v.applications.length]);

  const editorOpen = v.creating || Boolean(v.selectedId);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ManufacturingNamedVariantsListCard
        enabled={enabled}
        saveBusy={v.saveBusy}
        listBusy={v.listBusy}
        variants={v.variants}
        selectedId={v.selectedId}
        creating={v.creating}
        bulk={bulk}
        exportBusy={exp.busy}
        localError={v.localError}
        onBeginCreate={v.beginCreate}
        onBulkImport={() => setShowBulkImport(true)}
        onReload={() => void v.reloadList()}
        onPickVariant={v.pickVariant}
        onExportSelected={() => exp.exportCsv(bulk.selectedItems)}
        onExportAll={() => exp.exportCsv(v.variants)}
      />

      <div className="space-y-4">
        {v.creating ? (
          <SectionCard
            title="Create named variant"
            rightSlot={
              <button type="button" className={ghostBtnClass} onClick={v.closeEditor}>
                Close
              </button>
            }
          >
            <div className="space-y-3">
              <ManufacturingNamedVariantMetaFields
                name={v.newName}
                code={v.newCode}
                description={v.newDescription}
                onNameChange={v.setNewName}
                onCodeChange={v.setNewCode}
                onDescriptionChange={v.setNewDescription}
                codeHint="Generated from the name; change anytime before create."
                nameAutoFocus
              />
              <ManufacturingNamedVariantExampleRecipePicker
                recipes={recipes}
                disabled={v.saveBusy}
                onSeeded={(seed) =>
                  v.onSeedFromExample(
                    seed
                      ? { transform: seed.transform, recipeSlots: seed.recipeSlots }
                      : null,
                  )
                }
              />
              <ManufacturingNamedVariantDefaultTransformEditor
                config={v.newTransform ?? { mode: "delta", delta_lines: [] }}
                orgSlotLabels={orgSlotLabels}
                prioritySlotLabels={v.activePrioritySlots}
                materials={materials}
                onChange={v.setNewTransform}
              />
              <button
                type="button"
                className={mfgPrimaryBtnClass}
                disabled={v.saveBusy || !v.newName.trim()}
                onClick={() => void v.onCreate()}
              >
                {v.saveBusy ? "Saving…" : "Create"}
              </button>
            </div>
          </SectionCard>
        ) : null}

        {!v.creating && v.selectedId ? (
          <>
            <SectionCard
              title="Variant details"
              rightSlot={
                <button type="button" className={ghostBtnClass} onClick={v.closeEditor}>
                  Close
                </button>
              }
            >
              {v.detailBusy ? (
                <div className="text-sm text-gray-500">Loading…</div>
              ) : (
                <div className="space-y-3">
                  <ManufacturingNamedVariantMetaFields
                    name={v.editName}
                    code={v.editCode}
                    description={v.editDescription}
                    status={v.editStatus}
                    onNameChange={v.setEditName}
                    onCodeChange={v.setEditCode}
                    onDescriptionChange={v.setEditDescription}
                    onStatusChange={v.setEditStatus}
                    showStatus
                    canArchive={canArchive}
                  />
                  <ManufacturingNamedVariantDefaultTransformEditor
                    key={v.selectedId}
                    config={v.editTransform}
                    orgSlotLabels={orgSlotLabels}
                    prioritySlotLabels={v.activePrioritySlots}
                    materials={materials}
                    onChange={v.setEditTransform}
                    defaultCollapsed
                    namedVariantId={v.selectedId}
                    onError={(msg) => v.setLocalError(msg)}
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={mfgPrimaryBtnClass}
                      disabled={v.saveBusy}
                      onClick={() => void v.onSaveMeta()}
                    >
                      Save variant
                    </button>
                    {canArchive && v.editStatus !== "archived" ? (
                      <button
                        type="button"
                        className={ghostBtnClass}
                        disabled={v.saveBusy}
                        onClick={() => void v.onArchive()}
                      >
                        Archive
                      </button>
                    ) : null}
                    {canDelete ? (
                      <button
                        type="button"
                        className={ghostBtnClass}
                        disabled={v.saveBusy}
                        onClick={() => void v.onDeleteVariant()}
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                </div>
              )}
            </SectionCard>

            <SectionCard
              title="Apply to recipes"
              description="Materializes the variant template per recipe (binds transforms to real codes, keeps deltas). AI may suggest prices only. Confirm each application before deals use them."
            >
              <ManufacturingNamedVariantApplyRecipesPicker
                recipes={recipes}
                selectedIds={v.applyParentIds}
                onSelectedIdsChange={v.setApplyParentIds}
                description={v.applyDescription}
                onDescriptionChange={v.setApplyDescription}
                saveBusy={v.saveBusy}
                applyBusy={v.applyBusy}
                applyRecipeCount={v.applyRecipeCount}
                onApply={() => void v.onApplyToRecipes()}
                assignedByParentId={preview.assignedByParentId}
              />
            </SectionCard>

            <SectionCard
              title="Applications"
              description={
                applicationsOpen
                  ? "Per-recipe materials — edit and confirm here (does not change the variant template)."
                  : `${v.applications.length} application${
                      v.applications.length === 1 ? "" : "s"
                    } · click Show to review`
              }
              rightSlot={
                <div className="flex flex-wrap items-center gap-1">
                  {v.applications.length > 0 ? (
                    <button
                      type="button"
                      className={ghostBtnClass}
                      disabled={v.saveBusy}
                      title="Re-apply variant default materials to all applications"
                      onClick={() => {
                        const n = v.applications.length;
                        const ok = window.confirm(
                          `Reset ${n} application${n === 1 ? "" : "s"} to this variant's current default materials effect?\n\n` +
                            "This will unconfirm existing applications, replace their materials effects with the variant default (recomputed per recipe), and require reconfirmation. Sell price deltas are kept.",
                        );
                        if (!ok) return;
                        void v.onResetApplications();
                      }}
                    >
                      {v.saveBusy ? "Resetting…" : "Reset"}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={ghostBtnClass}
                    aria-expanded={applicationsOpen}
                    onClick={() => setApplicationsOpen((o) => !o)}
                  >
                    {applicationsOpen ? "Hide" : "Show"}
                  </button>
                </div>
              }
            >
              <div className={applicationsOpen ? undefined : "hidden"}>
                <ManufacturingNamedVariantApplicationsList
                  namedVariantId={v.selectedId}
                  applications={v.applications}
                  templateMode={v.editTransform.mode}
                  materials={materials}
                  orgSlotLabels={orgSlotLabels}
                  saveBusy={v.saveBusy}
                  previewBusy={preview.previewBusy}
                  onPreview={(id) => void preview.openPreview(id)}
                  onConfirm={(id) => void v.onConfirmApplication(id)}
                  onConfirmMany={(ids) => void v.onConfirmApplications(ids)}
                  onDelete={(id) => void v.onDeleteApplication(id)}
                  onUpdatePrices={(id, patch) => void v.onUpdateApplicationPrices(id, patch)}
                  onUpdateEffect={(id, effect) => void v.onUpdateApplicationEffect(id, effect)}
                />
              </div>
            </SectionCard>
          </>
        ) : null}

        {!editorOpen ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-400">
            Choose a named variant or create a new one.
          </div>
        ) : null}
      </div>

      <ManufacturingNamedVariantPreviewModal
        model={preview.previewModel}
        busy={preview.previewBusy}
        error={preview.previewError}
        onClose={preview.closePreview}
      />

      {showBulkImport ? (
        <ManufacturingNamedVariantsBulkImportModal
          existingVariants={v.variants}
          onClose={() => setShowBulkImport(false)}
          onImported={() => void v.reloadList()}
        />
      ) : null}
    </div>
  );
}
